import type { DirectoryStatus } from '../../api/types.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { color, fontSize } from '../../theme/tokens.js';

/**
 * The manager's name, with an amber sign when they CANNOT be sent the weekly report (FR-AL15,
 * PRD-Alerts §6.5.1, owner request AL-25).
 *
 * ⚠ `null` draws nothing: it means the snapshot predates the HR-directory check, which is unknown,
 * not a problem — a sign beside every name until the next refresh would be a false alarm.
 */
export function managerWarning(name: string | null, status: DirectoryStatus): string | null {
  const who = name ?? 'This manager';
  if (status === 'inactive') return `${who} is no longer active in the HR directory, so they are not sent the weekly team report.`;
  if (status === 'not_found') return `${who} could not be found in the HR directory, so they are not sent the weekly team report.`;
  return null;
}

export function ManagerCell({ name, status }: { name: string | null; status: DirectoryStatus }) {
  const warning = managerWarning(name, status);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: fontSize.control, color: color.inkBody }}>
      <span>{name ?? '—'}</span>
      {warning ? (
        <span
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
          }}
          style={{ display: 'inline-flex' }}
        >
          <Disclosure accessibleName={warning} panel={<span>{warning}</span>}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color.warning} strokeWidth="2.2" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <path d="M12 9v4M12 17h.01" />
            </svg>
          </Disclosure>
        </span>
      ) : null}
    </span>
  );
}
